/**
 * SEO HEADING STRUCTURE - Homepage (Index)
 * ========================================
 * 
 * TITLE: CSW - Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Free virtual study rooms and online workspace for students...
 * 
 * H1 TAGS (Multiple for keyword targeting):
 * - "Productivity Tool" (Hero component - inline)
 * - "Online Workspace" (Hero component - inline)
 * - "Study With Me" (StudyModes component)
 * - "Study With Strangers" (StudyModes component)
 * - "Study Stream" (StudyStream component)
 * 
 * H2 TAGS:
 * - "Study Modes" (Modes component)
 * - "Study Rooms: Real-Time Focus Hubs" (StudyRooms component)
 * - "Ways to Study Together" (StudyModes component)
 * - "Studyverse Alternative" (StudyverseAlternative component)
 * - "Ready to Transform Your Productivity?" (CTA component)
 */

import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Modes } from "@/components/Modes";
import { StudyRooms } from "@/components/StudyRooms";
import { StudyStream } from "@/components/StudyStream";
import { StudyModes } from "@/components/StudyModes";
import { StudyverseAlternative } from "@/components/StudyverseAlternative";
import { CTA } from "@/components/CTA";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const homepageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://csw.live/#webpage",
      "url": "https://csw.live/",
      "name": "CSW - Online Workspace | Productivity Tool | Study Stream | Study With Me",
      "description": "Free virtual study rooms and online workspace. Join unlimited study streams with cameras on, shared timers and ritual check-ins. CSW is a community-powered productivity hub for students and professionals who thrive when connected.",
      "inLanguage": "en",
      "dateModified": "2026-01-30",
      "isPartOf": {
        "@type": "WebSite",
        "@id": "https://csw.live/#website",
        "url": "https://csw.live/",
        "name": "CSW",
        "publisher": {
          "@type": "Organization",
          "name": "CSW",
          "url": "https://csw.live"
        }
      },
      "about": {
        "@type": "SoftwareApplication",
        "name": "CSW",
        "applicationCategory": "ProductivityApplication",
        "operatingSystem": "Web Browser",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "5",
          "ratingCount": "3000",
          "bestRating": "5",
          "worstRating": "1"
        }
      },
      "keywords": [
        "online workspace",
        "productivity tool",
        "study stream",
        "study with me",
        "study with strangers",
        "virtual study room",
        "pomodoro timer",
        "body doubling",
        "social accountability",
        "focus rooms",
        "study together online",
        "collaborative learning",
        "video study sessions",
        "studyverse alternative",
        "free study app",
        "24/7 study rooms"
      ],
      "mentions": [
        {
          "@type": "Thing",
          "name": "Online Workspace",
          "description": "Cloud-based productivity workspace with AI automation, Kanban boards, and collaboration tools"
        },
        {
          "@type": "Thing",
          "name": "Productivity Tool",
          "description": "Suite of focus tools including Pomodoro timers, task management, and analytics"
        },
        {
          "@type": "Thing",
          "name": "Study Stream",
          "description": "Free unlimited study streams with cameras on, shared timers and ritual check-ins for visual accountability"
        },
        {
          "@type": "Thing",
          "name": "Study With Me",
          "description": "Virtual body-doubling sessions where viewers study alongside a host for motivation and accountability"
        },
        {
          "@type": "Thing",
          "name": "Study With Strangers",
          "description": "Global community study rooms connecting students and professionals worldwide for collaborative focus"
        },
        {
          "@type": "Thing",
          "name": "Pomodoro Technique",
          "description": "Time management method using focused work intervals with short breaks"
        },
        {
          "@type": "Thing",
          "name": "Body Doubling",
          "description": "Productivity strategy where working alongside others increases focus and accountability"
        },
        {
          "@type": "Thing",
          "name": "Social Facilitation",
          "description": "Psychological phenomenon where presence of others improves task performance"
        },
        {
          "@type": "Thing",
          "name": "Virtual Study Room",
          "description": "Online spaces mimicking library experience with video presence and digital tools"
        },
        {
          "@type": "Thing",
          "name": "Focus Sessions",
          "description": "Structured study periods with synchronized timers and accountability check-ins"
        },
        {
          "@type": "Thing",
          "name": "Gamification",
          "description": "Use of game elements like streaks and badges to increase engagement and motivation"
        },
        {
          "@type": "Thing",
          "name": "Studyverse Alternative",
          "description": "Full productivity suite alternative to Studyverse with task management, timers, and community features"
        }
      ]
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://csw.live/#software",
      "name": "CSW - Chill Study Work",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Web Browser",
      "description": "Community-powered productivity hub with virtual study rooms, focus timers, task management and global community. Free forever with no limits.",
      "url": "https://csw.live",
      "featureList": [
        "Unlimited free study streams",
        "24/7 virtual study rooms",
        "Pomodoro timer with group sync",
        "Kanban task management",
        "Video presence and screen sharing",
        "Global study community",
        "Chill, Study, and Work modes",
        "Flashcards and quizzes",
        "Calendar reminders",
        "Progress analytics"
      ],
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "ratingCount": "3000",
        "bestRating": "5",
        "worstRating": "1"
      },
      "author": {
        "@type": "Organization",
        "name": "CSW",
        "url": "https://csw.live"
      }
    },
    {
      "@type": "Organization",
      "@id": "https://csw.live/#organization",
      "name": "CSW",
      "url": "https://csw.live",
      "logo": "https://csw.live/favicon.png",
      "description": "Community-powered productivity platform for students, professionals, and lifelong learners"
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://csw.live/#breadcrumb",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://csw.live/"
        }
      ]
    }
  ]
};

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="CSW - Online Workspace | Productivity Tool | Study Stream | Study With Me"
        description="Free virtual study rooms and online workspace. Join unlimited study streams with cameras on, shared timers and ritual check-ins. CSW is a community-powered productivity hub for students and professionals who thrive when connected."
        keywords="online workspace, virtual study room, study stream, productivity tool, pomodoro timer, study with me, study with strangers, online study room, study together, body doubling, accountability"
        canonicalUrl="/"
        isHomepage={true}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(homepageSchema)}
        </script>
      </Helmet>
      <Header />
      <main>
        <Hero />
        <Modes />
        <StudyRooms />
        <StudyStream />
        <StudyModes />
        <StudyverseAlternative />
        <CTA />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
